import {Component, OnDestroy, OnInit} from '@angular/core';
import {VisitorsService} from '../../../services/visitors.service';
import {PaymentService} from '../../../services/payment.service';
import {NgbCalendar, NgbDateStruct, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {Visitor} from '../../../classes/Visitor';
import {VisitorsEditComponent} from '../visitors-edit/visitors-edit.component';
import {Office} from '../../../classes/Office';
import {OfficeService} from '../../../services/office.service';
import {FormGroup, Validators, FormBuilder} from '@angular/forms';
import {VisitorStatus} from '../../../enums/VisitorStatus';
import {Page} from '../../../classes/Page';
import {LanguageService} from '../../../services/language.service';

@Component({
  selector: 'app-visitors-management',
  templateUrl: './visitors-management.component.html',
  styleUrls: ['./visitors-management.component.scss']
})
export class VisitorsManagementComponent implements OnInit {
  public visitors: Visitor[];
  public offices: Map<number, Office>;
  public filterForm: FormGroup;
  public visitorStatuses = VisitorStatus;
  public visitorStatusKeys: string[];
  public errorMessage;
  public pageNumber = 1;
  public totalElements = 0;
  public pageSizes = [10, 20, 50];
  public pageSize = this.pageSizes[0];

  constructor(private visitorsService: VisitorsService,
              private paymentService: PaymentService,
              private modalService: NgbModal,
              private formBuilder: FormBuilder,
              private calendar: NgbCalendar,
              public languageService: LanguageService,
              private officeService: OfficeService) {}

  ngOnInit(): void {
    this.visitorStatusKeys = Object.keys(VisitorStatus);
    const today = new Date();
    const startDate = this.convertDate(new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1, 0, 0, 0)));
    const endDate = this.calendar.getToday();

    this.filterForm = this.formBuilder.group({
      startDate: [startDate, Validators.required],
      endDate: [endDate, Validators.required],
      visitorStatus: ['ALL']
    });

    this.filterForm.controls.startDate.valueChanges.subscribe(() => this.loadVisitors());
    this.filterForm.controls.endDate.valueChanges.subscribe(() => this.loadVisitors());
    this.filterForm.controls.visitorStatus.valueChanges.subscribe(() => this.loadVisitors());

    this.loadVisitors();

    this.officeService.getAll()
      .subscribe(response => this.offices = new Map(response.map(office => [office.id, office])));
  }

  loadVisitors(): void {
    const startDate = this.convertNgbDate(this.filterForm.controls.startDate.value, 0, 0, 0);
    const endDate = this.convertNgbDate(this.filterForm.controls.endDate.value, 23, 59, 59);
    let visitorStatus = this.filterForm.controls.visitorStatus.value;

    if (visitorStatus === 'ALL') {
      visitorStatus = null;
    }

    this.visitorsService.getAllVisitors(startDate, endDate, visitorStatus, this.pageNumber - 1, this.pageSize)
      .subscribe({
        next: (response: Page<Visitor>) => {
          this.visitors = response.content;
          this.totalElements = response.totalElements;
        }
      });
  }

  openAddVisitorModal(): void {
    const modalRef = this.modalService.open(VisitorsEditComponent);
    modalRef.componentInstance.order = { id: null, user: {} };
    modalRef.componentInstance.showAdminFields = true;
    modalRef.componentInstance.adminVisitor = true;

    modalRef.result.then((result) => {
      if (result) {
        this.loadVisitors();
      }
    }, (reason) => {
      modalRef.dismiss(reason);
    });
  }

  openEditVisitorModal(visitor: Visitor): void {
    const modalRef = this.modalService.open(VisitorsEditComponent);
    modalRef.componentInstance.order = { id: visitor.order.id, user: visitor }; // pass visitor data into modal

    modalRef.result.then((result) => {
      if (result) {
        this.loadVisitors();
      }
    }, (reason) => {
      // modal close handling
    });
  }

  updateVisitor(visitor: Visitor): void {
    this.openEditVisitorModal(visitor);
  }

  deleteVisitor(id: number): void {
    this.visitorsService.delete(id).subscribe(() => {
      this.loadVisitors();
    });
  }

  getOffice(officeId: number): string {
    const office = this.offices.get(officeId);
    return office.city + ', ' + office.address;
  }

  convertDate(date: Date): NgbDateStruct {
    return date ? {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate()
    } : null;
  }

  convertNgbDate(ngbDate: NgbDateStruct, hours: number, minutes: number, seconds: number): Date {
    const date = new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day, hours, minutes, seconds);

    if (hours === 0) {
      date.setDate( date.getDate() );
    }

    return ngbDate ? date : null;
  }

  handlePageChange(event): void {
    this.pageNumber = event;
    this.loadVisitors();
  }

  handlePageSizeChange(event): void {
    this.pageSize = event.target.value;
    this.pageNumber = 1;
    this.loadVisitors();
  }

  getMinLimit(): number {
    return this.pageSize * (this.pageNumber - 1) + 1;
  }

  getMaxLimit(): number {
    const maxLimit = this.pageSize * this.pageNumber;
    return maxLimit <= this.totalElements ? maxLimit : this.totalElements;
  }
}
